import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    Plus, 
    Edit2, 
    Trash2, 
    Search, 
    Users,
    Phone,
    MapPin,
    Calendar,
    Camera,
    X as XIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AREAS = [
    { value: 'produccion', label: 'Producción' },
    { value: 'almacen', label: 'Almacén' },
    { value: 'maquila', label: 'Maquila' },
    { value: 'lavanderia', label: 'Lavandería' },
    { value: 'administracion', label: 'Administración' }
];

const TIPOS_TRABAJADOR = [
    { value: 'empleado', label: 'Empleado General' },
    { value: 'cortador', label: 'Cortador' },
    { value: 'maquilador', label: 'Maquilador' },
    { value: 'lavanderia', label: 'Lavanderia' },
    { value: 'supervision', label: 'Supervision en area' }
];

const initialForm = {
    nombre: '',
    telefono: '',
    direccion: '',
    puesto: '',
    area: '',
    fecha_ingreso: new Date().toISOString().split('T')[0],
    estatus: 'activo',
    tipo_trabajador: 'empleado',
    precio_por_pieza: 0,
    sueldo: 0,
    observaciones: '',
    nombre_reloj: ''
};

export const Empleados = () => {
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [fotoFile, setFotoFile] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchEmpleados();
    }, []);

    const fetchEmpleados = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/empleados`);
            setEmpleados(response.data);
        } catch (error) {
            console.error('Error fetching empleados:', error);
            toast.error('Error al cargar empleados');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.puesto || !form.area) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }

        try {
            let empId = editingId;
            if (editingId) {
                await axios.put(`${API_URL}/api/empleados/${editingId}`, form);
                toast.success('Empleado actualizado exitosamente');
            } else {
                const res = await axios.post(`${API_URL}/api/empleados`, form);
                empId = res.data.id;
                toast.success('Empleado creado exitosamente');
            }

            // Upload photo if selected
            if (fotoFile && empId) {
                const formData = new FormData();
                formData.append('file', fotoFile);
                await axios.post(`${API_URL}/api/empleados/${empId}/foto`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            setModalOpen(false);
            setForm(initialForm);
            setEditingId(null);
            setFotoFile(null);
            setFotoPreview(null);
            fetchEmpleados();
        } catch (error) {
            console.error('Error saving empleado:', error);
            toast.error('Error al guardar empleado');
        }
    };

    const handleEdit = (empleado) => {
        setForm({
            nombre: empleado.nombre,
            telefono: empleado.telefono,
            direccion: empleado.direccion,
            puesto: empleado.puesto,
            area: empleado.area,
            fecha_ingreso: empleado.fecha_ingreso,
            estatus: empleado.estatus || 'activo',
            tipo_trabajador: empleado.tipo_trabajador || 'empleado',
            precio_por_pieza: empleado.precio_por_pieza || 0,
            sueldo: empleado.sueldo || 0,
            observaciones: empleado.observaciones,
            nombre_reloj: empleado.nombre_reloj || ''
        });
        setEditingId(empleado.id);
        setFotoFile(null);
        setFotoPreview(empleado.foto_url ? `${API_URL}${empleado.foto_url}` : null);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de dar de baja a este empleado?')) return;
        
        try {
            await axios.delete(`${API_URL}/api/empleados/${id}`);
            toast.success('Empleado dado de baja exitosamente');
            fetchEmpleados();
        } catch (error) {
            console.error('Error deleting empleado:', error);
            toast.error('Error al dar de baja empleado');
        }
    };

    const filteredEmpleados = empleados.filter(emp =>
        emp.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.puesto.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.area.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getAreaLabel = (area) => {
        const found = AREAS.find(a => a.value === area);
        return found ? found.label : area;
    };

    const handleFotoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFotoFile(file);
        setFotoPreview(URL.createObjectURL(file));
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    };

    const EmployeeAvatar = ({ emp, size = 'sm' }) => {
        const s = size === 'lg' ? 'w-16 h-16 text-lg' : 'w-8 h-8 text-xs';
        if (emp.foto_url) {
            return <img src={`${API_URL}${emp.foto_url}`} alt={emp.nombre} className={`${s} rounded-full object-cover border-2 border-zinc-700`} />;
        }
        return (
            <div className={`${s} rounded-full bg-amber-400/15 flex items-center justify-center font-bold text-amber-400`}>
                {getInitials(emp.nombre)}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="empleados-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Gestión de Empleados
                    </h1>
                    <p className="text-zinc-400 mt-1">Administre el personal de la empresa</p>
                </div>
                <Button
                    onClick={() => {
                        setForm(initialForm);
                        setEditingId(null);
                        setFotoFile(null);
                        setFotoPreview(null);
                        setModalOpen(true);
                    }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="add-empleado-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nuevo Empleado
                </Button>
            </div>

            {/* Search */}
            <Card className="card">
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                            placeholder="Buscar por nombre, puesto o área..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-empleados"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="card">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Nombre</TableHead>
                                    <TableHead className="table-header">Puesto</TableHead>
                                    <TableHead className="table-header">Tipo</TableHead>
                                    <TableHead className="table-header">$/Pieza</TableHead>
                                    <TableHead className="table-header">Área</TableHead>
                                    <TableHead className="table-header">Estatus</TableHead>
                                    <TableHead className="table-header text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredEmpleados.length > 0 ? (
                                    filteredEmpleados.map((empleado) => (
                                        <TableRow key={empleado.id} className="table-row" data-testid={`empleado-row-${empleado.id}`}>
                                            <TableCell className="table-cell font-medium">
                                                <div className="flex items-center gap-3">
                                                    <EmployeeAvatar emp={empleado} />
                                                    {empleado.nombre}
                                                </div>
                                            </TableCell>
                                            <TableCell className="table-cell">{empleado.puesto}</TableCell>
                                            <TableCell className="table-cell">
                                                <span className={
                                                    empleado.tipo_trabajador === 'cortador' ? 'badge-info' :
                                                    empleado.tipo_trabajador === 'maquilador' ? 'badge-warning' :
                                                    empleado.tipo_trabajador === 'lavanderia' ? 'badge-success' :
                                                    empleado.tipo_trabajador === 'supervision' ? 'badge-info' :
                                                    'px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600'
                                                }>
                                                    {TIPOS_TRABAJADOR.find(t => t.value === empleado.tipo_trabajador)?.label || empleado.tipo_trabajador}
                                                </span>
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {(empleado.tipo_trabajador === 'empleado' || empleado.tipo_trabajador === 'supervision') 
                                                    ? (empleado.sueldo > 0 ? `$${empleado.sueldo.toFixed(2)} sueldo` : '-')
                                                    : (empleado.precio_por_pieza > 0 ? `$${empleado.precio_por_pieza.toFixed(2)}/pza` : '-')
                                                }
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                <span className="badge-info">{getAreaLabel(empleado.area)}</span>
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                <span className={empleado.estatus === 'activo' ? 'badge-success' : 'badge-error'}>
                                                    {empleado.estatus === 'activo' ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="table-cell text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(empleado)}
                                                        data-testid={`edit-empleado-${empleado.id}`}
                                                    >
                                                        <Edit2 className="w-4 h-4 text-amber-400" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(empleado.id)}
                                                        data-testid={`delete-empleado-${empleado.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-400" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-zinc-400">
                                            No se encontraron empleados
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="empleado-modal">
                    <DialogHeader>
                        <DialogTitle >
                            {editingId ? 'Editar Empleado' : 'Nuevo Empleado'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Photo Upload */}
                        <div className="flex flex-col items-center gap-3">
                            <div className="relative">
                                {fotoPreview ? (
                                    <div className="relative">
                                        <img src={fotoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-zinc-700" />
                                        <button type="button" onClick={() => { setFotoFile(null); setFotoPreview(null); }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                                            <XIcon className="w-3 h-3 text-white" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-20 h-20 rounded-full bg-zinc-800 border-2 border-dashed border-zinc-600 flex items-center justify-center">
                                        <Camera className="w-6 h-6 text-zinc-500" />
                                    </div>
                                )}
                            </div>
                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFotoSelect} className="hidden" data-testid="input-foto" />
                            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="text-xs" data-testid="upload-foto-btn">
                                <Camera className="w-3 h-3 mr-1" />
                                {fotoPreview ? 'Cambiar Foto' : 'Agregar Foto'}
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Nombre Completo *</Label>
                                <Input
                                    value={form.nombre}
                                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                    placeholder="Nombre del empleado"
                                    data-testid="input-nombre"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Puesto *</Label>
                                    <Input
                                        value={form.puesto}
                                        onChange={(e) => setForm({ ...form, puesto: e.target.value })}
                                        placeholder="Puesto"
                                        data-testid="input-puesto"
                                    />
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Área *</Label>
                                    <Select value={form.area} onValueChange={(value) => setForm({ ...form, area: value })}>
                                        <SelectTrigger data-testid="select-area">
                                            <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {AREAS.map((area) => (
                                                <SelectItem key={area.value} value={area.value}>
                                                    {area.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Tipo de Trabajador *</Label>
                                    <Select value={form.tipo_trabajador} onValueChange={(value) => setForm({ ...form, tipo_trabajador: value })}>
                                        <SelectTrigger data-testid="select-tipo-trabajador">
                                            <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TIPOS_TRABAJADOR.map((tipo) => (
                                                <SelectItem key={tipo.value} value={tipo.value}>
                                                    {tipo.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="form-group">
                                    {form.tipo_trabajador === 'empleado' || form.tipo_trabajador === 'supervision' ? (
                                        <>
                                            <Label className="form-label">Sueldo ($)</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={form.sueldo}
                                                onChange={(e) => setForm({ ...form, sueldo: parseFloat(e.target.value) || 0 })}
                                                placeholder="0.00"
                                                data-testid="input-sueldo"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Label className="form-label">Precio por Pieza ($)</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={form.precio_por_pieza}
                                                onChange={(e) => setForm({ ...form, precio_por_pieza: parseFloat(e.target.value) || 0 })}
                                                placeholder="0.00"
                                                data-testid="input-precio-pieza"
                                            />
                                            {form.precio_por_pieza > 0 && (
                                                <p className="text-xs text-amber-400 mt-1">La deuda se calculara con este precio al registrar trabajo</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Teléfono</Label>
                                    <Input
                                        value={form.telefono}
                                        onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                                        placeholder="Teléfono"
                                        data-testid="input-telefono"
                                    />
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Fecha de Ingreso</Label>
                                    <Input
                                        type="date"
                                        value={form.fecha_ingreso}
                                        onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })}
                                        data-testid="input-fecha"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <Label className="form-label">Dirección</Label>
                                <Input
                                    value={form.direccion}
                                    onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                                    placeholder="Dirección"
                                    data-testid="input-direccion"
                                />
                            </div>

                            <div className="form-group">
                                <Label className="form-label">Observaciones</Label>
                                <Input
                                    value={form.observaciones}
                                    onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                                    placeholder="Notas adicionales"
                                    data-testid="input-observaciones"
                                />
                            </div>

                            <div className="form-group">
                                <Label className="form-label">Nombre en Reloj Biometrico</Label>
                                <Input
                                    value={form.nombre_reloj}
                                    onChange={(e) => setForm({ ...form, nombre_reloj: e.target.value })}
                                    placeholder="Tal como aparece en el Excel del reloj (ej: jason)"
                                    data-testid="input-nombre-reloj"
                                />
                                <p className="text-xs text-zinc-500 mt-1">Para vincular automaticamente con el archivo de asistencia</p>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-empleado-btn">
                                {editingId ? 'Actualizar' : 'Guardar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
